import { Box, UserRound, Users, Handshake, Banknote, Clock, MapPin } from "lucide-react";
import { ZMENA_HODNOTY, zmenaHodnoty, HODNOTA_HODNOTY, hodnotaPasmo, bezDiakritiky } from "./filtr";

/**
 * Pole filtru sdílená seznamy (Klienti, Obchod, Nabídky, Servis). Každé pole:
 * { klic, nazev, rod, ikona, tvary, hodnoty(polozky) → [{ v, label }], hodnotyPolozky(p) → [v] }.
 */
const NIKDO = "_nikdo";
const serad = (a) => a.sort((x, y) => x.label.localeCompare(y.label, "cs"));

/** Hodnoty z načtených položek (technologie, klienti, města…), seřazené podle abecedy. */
function zPolozek(polozky, fn) {
  const m = new Map();
  for (const p of polozky) for (const [v, label] of fn(p)) if (v && !m.has(v)) m.set(v, label || v);
  return serad([...m].map(([v, label]) => ({ v, label })));
}

export const poleTechnologie = (nazvy) => ({
  klic: "technologie", nazev: "Technologie", rod: "z", ikona: Box, tvary: ["technologie", "technologie", "technologií"],
  hodnoty: (polozky) => zPolozek(polozky, (p) => nazvy(p).map((n) => [n, n])),
  hodnotyPolozky: (p) => nazvy(p),
});

/** Člověk z týmu (obchodník, vedoucí montáže): „Já“ první, pak abecedně, „Nikdo“ když je potřeba. */
export const poleClovek = ({ klic, nazev, ikona = UserRound, tym, me, id }) => ({
  klic, nazev, rod: "m", ikona, tvary: ["člověk", "lidé", "lidí"],
  hodnoty: (polozky) => {
    const lide = serad(tym.filter((t) => t.id !== me).map((t) => ({ v: t.id, label: t.name })));
    const nikdo = polozky.some((p) => !id(p)) ? [{ v: NIKDO, label: "Nikdo" }] : [];
    return [...(tym.some((t) => t.id === me) ? [{ v: me, label: "Já" }] : []), ...lide, ...nikdo];
  },
  hodnotyPolozky: (p) => [id(p) || NIKDO],
  popis: (v) => (v === NIKDO ? "Nikdo" : v),
});

export const poleKlient = (id, jmeno) => ({
  klic: "klient", nazev: "Klient", rod: "m", ikona: Users, tvary: ["klient", "klienti", "klientů"],
  hodnoty: (polozky) => zPolozek(polozky, (p) => [[id(p), jmeno(p)]]),
  hodnotyPolozky: (p) => (id(p) ? [id(p)] : []),
});

export const polePartner = (partner) => ({
  klic: "partner", nazev: "Obchodní partner", rod: "m", ikona: Handshake, tvary: ["partner", "partneři", "partnerů"],
  hodnoty: (polozky) => [...zPolozek(polozky, (p) => (partner(p) ? [[partner(p).id, partner(p).nazev]] : [])),
    ...(polozky.some((p) => !partner(p)) ? [{ v: NIKDO, label: "Bez partnera" }] : [])],
  hodnotyPolozky: (p) => [partner(p)?.id || NIKDO],
  popis: (v) => (v === NIKDO ? "Bez partnera" : v),
});

export const poleHodnota = (castka) => ({
  klic: "hodnota", nazev: "Hodnota", rod: "z", ikona: Banknote, tvary: ["pásmo", "pásma", "pásem"],
  hodnoty: () => HODNOTA_HODNOTY,
  hodnotyPolozky: (p) => hodnotaPasmo(castka(p)),
});

export const poleZmena = (datum, { klic = "zmena", nazev = "Poslední změna" } = {}) => ({
  klic, nazev, rod: "z", ikona: Clock, tvary: ["období", "období", "období"],
  hodnoty: () => ZMENA_HODNOTY,
  hodnotyPolozky: (p) => zmenaHodnoty(datum(p)),
});

export const poleMesto = (mesto) => ({
  klic: "mesto", nazev: "Město", rod: "s", ikona: MapPin, tvary: ["město", "města", "měst"],
  // „Brno“ a „brno “ je jedno město
  hodnoty: (polozky) => zPolozek(polozky, (p) => (mesto(p) ? [[bezDiakritiky(mesto(p).trim()), mesto(p).trim()]] : [])),
  hodnotyPolozky: (p) => (mesto(p) ? [bezDiakritiky(mesto(p).trim())] : []),
});

/** Pole s pevnými hodnotami (Typ, Stav, Ano/Ne…). */
export const polePevne = ({ klic, nazev, rod = "m", ikona, hodnoty, tvary, hodnotyPolozky }) => ({
  klic, nazev, rod, ikona, tvary, hodnoty: () => hodnoty, hodnotyPolozky,
});
